/**
 * Tool category metadata.
 *
 * Every category name emitted by the backend (see
 * `django-backend/AI/agents/tools/**`) gets a human-friendly label, an icon,
 * a MUI colour and a high-level group so the Tools tab can render them
 * consistently. Unknown categories fall back to a neutral default.
 */

import React from 'react';
import {
  Public as WebIcon,
  CloudDownload as DataIcon,
  Warehouse as WarehouseIcon,
  Code as CodeIcon,
  Science as LabIcon,
  ModelTraining as LlmIcon,
  SmartToy as AgentIcon,
  Groups as DelegationIcon,
  Engineering as ExpertIcon,
  Memory as BotIcon,
  Search as DiscoveryIcon,
  ShowChart as TradeAnalysisIcon,
  PlayCircleOutline as TradingActionsIcon,
  Timeline as BacktestIcon,
  AccountBalance as AccountIcon,
  Speed as IndicatorIcon,
  PlayArrow as ExecutionIcon,
  Storage as StorageIcon,
  Inventory as GlobalStorageIcon,
  Extension as SnippetIcon,
  Share as SharedInputIcon,
  Psychology as MemoryIcon,
  MenuBook as KnowledgeIcon,
  Hub as GraphIcon,
  Schedule as ScheduleIcon,
  Settings as SystemIcon,
  AutoAwesome as GenericIcon,
} from '@mui/icons-material';
import type { SvgIconProps } from '@mui/material';

export type ToolCategoryGroup =
  | 'trading'
  | 'data'
  | 'knowledge'
  | 'agents'
  | 'development'
  | 'system'
  | 'other';

export interface ToolCategoryMeta {
  /** Canonical backend slug (e.g. "data_fetching"). */
  id: string;
  /** Human-friendly label (e.g. "Data fetching"). */
  label: string;
  /** Short description used in tooltips. */
  description: string;
  /** High-level group for bucketing on the UI. */
  group: ToolCategoryGroup;
  icon: React.ReactElement<SvgIconProps>;
  /** MUI colour intent for chips / avatars. */
  color:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'info'
    | 'warning'
    | 'error';
}

export const TOOL_CATEGORY_GROUP_META: Record<
  ToolCategoryGroup,
  { label: string; description: string; color: ToolCategoryMeta['color']; order: number }
> = {
  trading: {
    label: 'Trading',
    description: 'Placing trades, analysing positions, backtesting and executions.',
    color: 'warning',
    order: 1,
  },
  data: {
    label: 'Data',
    description: 'Market data, warehouses, web requests.',
    color: 'info',
    order: 2,
  },
  knowledge: {
    label: 'Knowledge & Memory',
    description: 'Memory, knowledge base, graphs, snippets, shared inputs.',
    color: 'secondary',
    order: 3,
  },
  agents: {
    label: 'Agents & Experts',
    description: 'Managing agents, experts, bots and delegation.',
    color: 'primary',
    order: 4,
  },
  development: {
    label: 'Development',
    description: 'Code, model lab, LLM management.',
    color: 'default',
    order: 5,
  },
  system: {
    label: 'System',
    description: 'System, accounts, indicators, scheduling.',
    color: 'default',
    order: 6,
  },
  other: {
    label: 'Other',
    description: 'Uncategorised tools.',
    color: 'default',
    order: 99,
  },
};

const TOOL_CATEGORY_META: Record<string, ToolCategoryMeta> = {
  // ------------------------------ Trading ---------------------------------
  trading_actions: {
    id: 'trading_actions',
    label: 'Trading actions',
    description: 'Place, modify and close trades.',
    group: 'trading',
    icon: <TradingActionsIcon fontSize="small" />,
    color: 'warning',
  },
  trade_analysis: {
    id: 'trade_analysis',
    label: 'Trade analysis',
    description: 'Analyse trade history and performance.',
    group: 'trading',
    icon: <TradeAnalysisIcon fontSize="small" />,
    color: 'warning',
  },
  backtest_management: {
    id: 'backtest_management',
    label: 'Backtesting',
    description: 'Run and inspect strategy backtests.',
    group: 'trading',
    icon: <BacktestIcon fontSize="small" />,
    color: 'warning',
  },
  execution_management: {
    id: 'execution_management',
    label: 'Execution management',
    description: 'Monitor and control agent executions.',
    group: 'trading',
    icon: <ExecutionIcon fontSize="small" />,
    color: 'warning',
  },
  account_management: {
    id: 'account_management',
    label: 'Accounts',
    description: 'Inspect and manage trading accounts.',
    group: 'trading',
    icon: <AccountIcon fontSize="small" />,
    color: 'warning',
  },
  indicator_management: {
    id: 'indicator_management',
    label: 'Indicators',
    description: 'Configure and compute indicators.',
    group: 'trading',
    icon: <IndicatorIcon fontSize="small" />,
    color: 'warning',
  },

  // --------------------------------- Data ---------------------------------
  data_fetching: {
    id: 'data_fetching',
    label: 'Data fetching',
    description: 'Prices, news and market data lookups.',
    group: 'data',
    icon: <DataIcon fontSize="small" />,
    color: 'info',
  },
  data_warehouse: {
    id: 'data_warehouse',
    label: 'Data warehouse',
    description: 'Query large stored datasets.',
    group: 'data',
    icon: <WarehouseIcon fontSize="small" />,
    color: 'info',
  },
  web: {
    id: 'web',
    label: 'Web',
    description: 'Web search, scraping and HTTP.',
    group: 'data',
    icon: <WebIcon fontSize="small" />,
    color: 'info',
  },

  // ---------------------------- Knowledge ---------------------------------
  knowledge: {
    id: 'knowledge',
    label: 'Knowledge base',
    description: 'Service instructions and knowledge articles.',
    group: 'knowledge',
    icon: <KnowledgeIcon fontSize="small" />,
    color: 'secondary',
  },
  knowledge_graph: {
    id: 'knowledge_graph',
    label: 'Knowledge graph',
    description: 'Entity and relationship graph.',
    group: 'knowledge',
    icon: <GraphIcon fontSize="small" />,
    color: 'secondary',
  },
  memory_management: {
    id: 'memory_management',
    label: 'Memory',
    description: 'Agent long- and short-term memory.',
    group: 'knowledge',
    icon: <MemoryIcon fontSize="small" />,
    color: 'secondary',
  },
  global_storage_management: {
    id: 'global_storage_management',
    label: 'Global storage',
    description: 'Shared workspace key-value storage.',
    group: 'knowledge',
    icon: <GlobalStorageIcon fontSize="small" />,
    color: 'secondary',
  },
  storage_management: {
    id: 'storage_management',
    label: 'Storage',
    description: 'Per-agent storage.',
    group: 'knowledge',
    icon: <StorageIcon fontSize="small" />,
    color: 'secondary',
  },
  shared_input_management: {
    id: 'shared_input_management',
    label: 'Shared inputs',
    description: 'Shared prompts and input templates.',
    group: 'knowledge',
    icon: <SharedInputIcon fontSize="small" />,
    color: 'secondary',
  },
  snippet_management: {
    id: 'snippet_management',
    label: 'Snippets',
    description: 'Reusable prompt snippets.',
    group: 'knowledge',
    icon: <SnippetIcon fontSize="small" />,
    color: 'secondary',
  },

  // --------------------------- Agents & Experts ---------------------------
  agent_management: {
    id: 'agent_management',
    label: 'Agent management',
    description: 'Create and configure agents.',
    group: 'agents',
    icon: <AgentIcon fontSize="small" />,
    color: 'primary',
  },
  agent_delegation: {
    id: 'agent_delegation',
    label: 'Agent delegation',
    description: 'Invoke sub-agents and delegate work.',
    group: 'agents',
    icon: <DelegationIcon fontSize="small" />,
    color: 'primary',
  },
  expert_management: {
    id: 'expert_management',
    label: 'Experts',
    description: 'Manage expert systems and sub-assistants.',
    group: 'agents',
    icon: <ExpertIcon fontSize="small" />,
    color: 'primary',
  },
  bot_management: {
    id: 'bot_management',
    label: 'Bots',
    description: 'Manage trading bots and strategies.',
    group: 'agents',
    icon: <BotIcon fontSize="small" />,
    color: 'primary',
  },
  tool_discovery: {
    id: 'tool_discovery',
    label: 'Tool discovery',
    description: 'Find and browse available tools.',
    group: 'agents',
    icon: <DiscoveryIcon fontSize="small" />,
    color: 'primary',
  },

  // --------------------------- Development --------------------------------
  code_development: {
    id: 'code_development',
    label: 'Code',
    description: 'Read, edit and run code in sandboxes.',
    group: 'development',
    icon: <CodeIcon fontSize="small" />,
    color: 'default',
  },
  model_lab: {
    id: 'model_lab',
    label: 'Model lab',
    description: 'Train and evaluate ML models.',
    group: 'development',
    icon: <LabIcon fontSize="small" />,
    color: 'default',
  },
  llm_management: {
    id: 'llm_management',
    label: 'LLM management',
    description: 'Configure LLM providers and routing.',
    group: 'development',
    icon: <LlmIcon fontSize="small" />,
    color: 'default',
  },

  // ------------------------------ System ----------------------------------
  system_management: {
    id: 'system_management',
    label: 'System',
    description: 'System-level health and configuration.',
    group: 'system',
    icon: <SystemIcon fontSize="small" />,
    color: 'default',
  },
  schedule_management: {
    id: 'schedule_management',
    label: 'Schedules',
    description: 'Manage periodic schedules.',
    group: 'system',
    icon: <ScheduleIcon fontSize="small" />,
    color: 'default',
  },
  scheduling: {
    id: 'scheduling',
    label: 'Scheduling',
    description: 'Cron-style scheduling primitives.',
    group: 'system',
    icon: <ScheduleIcon fontSize="small" />,
    color: 'default',
  },
};

export const UNKNOWN_CATEGORY_META: ToolCategoryMeta = {
  id: 'unknown',
  label: 'Uncategorised',
  description: 'Tools without an explicit category.',
  group: 'other',
  icon: <GenericIcon fontSize="small" />,
  color: 'default',
};

/**
 * Return metadata for a raw backend category slug. Unknown slugs are
 * synthesised with a humanised label so the UI never breaks on new
 * categories introduced later.
 */
export function getToolCategoryMeta(raw?: string | null): ToolCategoryMeta {
  if (!raw) return UNKNOWN_CATEGORY_META;
  const key = String(raw).toLowerCase().trim();
  if (TOOL_CATEGORY_META[key]) return TOOL_CATEGORY_META[key];
  // Synthesise a meta entry so new backend categories still look clean.
  return {
    ...UNKNOWN_CATEGORY_META,
    id: key,
    label: key
      .split(/[_\s-]+/)
      .filter(Boolean)
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(' '),
  };
}
